import { ListChecks } from "lucide-react";

import { CountBadge } from "@/components/semantic-badges";
import { Button } from "@/components/ui/button";
import type { TrackerType } from "@/components/tracker-view-modal";
import { cn } from "@/lib/utils";
import { BUILT_IN_TRACKERS, type CustomTrackerInfo, type TrackerCounts } from "./tracker-config";

interface SubmittalTrackerStripProps {
  trackerCounts: TrackerCounts;
  customTrackerNames: CustomTrackerInfo[];
  onTrackerClick: (type: TrackerType, customTrackerName?: string) => void;
  className?: string;
}

function TrackerChip({
  label,
  count,
  icon: Icon = ListChecks,
  onClick,
}: {
  label: string;
  count: number;
  icon?: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  onClick: () => void;
}) {
  return (
    <Button type="button" variant="ghost" size="compact" onClick={onClick}>
      <Icon className="text-muted-foreground" aria-hidden />
      {label}
      <CountBadge value={count} max={9999} />
    </Button>
  );
}

/** Horizontal row of tracker shortcuts (opens each tracker's modal). */
export function SubmittalTrackerStrip({
  trackerCounts,
  customTrackerNames,
  onTrackerClick,
  className,
}: SubmittalTrackerStripProps) {
  return (
    <div role="group" aria-label="Trackers" className={cn("flex flex-wrap items-center gap-0.5", className)}>
      {BUILT_IN_TRACKERS.map((tracker) => (
        <TrackerChip
          key={tracker.type}
          label={tracker.label}
          icon={tracker.icon}
          count={trackerCounts[tracker.type]}
          onClick={() => onTrackerClick(tracker.type)}
        />
      ))}
      {customTrackerNames.map((tracker) => (
        <TrackerChip
          key={tracker.name}
          label={tracker.name}
          count={tracker.count}
          onClick={() => onTrackerClick("custom", tracker.name)}
        />
      ))}
    </div>
  );
}
